/**
 * CL-PRODUCT-SELECTIONS-005 — Client-safe product attachment file meta (portal preview).
 * Aligns publication file-access projections with canonical pmProjectFiles storage bindings.
 */

import {
  isLegacyPmProjectFileMainPath,
  isVersionedPmProjectFileStoragePath,
  projectFileUsesSecureStorage,
} from "./projectFileSecureStorageModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

/**
 * Documented binding order: versioned storagePath, storageOriginalPath, then legacy main path.
 * @see docs/PROJECT_FILE_STORAGE_MIGRATION.md (canonical path / legacy catch-all)
 */
export function resolveCanonicalProductClientFileStoragePath(fileRecord = {}) {
  const direct = safeStr(fileRecord.storagePath || fileRecord.storageOriginalPath);
  if (direct) return direct;
  const legacyMain = safeStr(fileRecord.path);
  if (legacyMain && isLegacyPmProjectFileMainPath(legacyMain)) return legacyMain;
  return "";
}

export function productClientPortalFileMetaHasStorageBinding(meta = {}) {
  const binding = safeStr(meta.storagePath || meta.path);
  if (binding) return true;
  if (safeStr(meta.url)) return true;
  return projectFileUsesSecureStorage(meta);
}

export function productClientPortalFileMetaNeedsCanonicalEnrichment(meta = {}) {
  return !productClientPortalFileMetaHasStorageBinding(meta);
}

/**
 * Merge canonical pmProjectFiles fields required for authorized Storage preview only.
 */
export function enrichProductClientPortalFileMetaFromCanonical(portalMeta = {}, canonical = {}) {
  const fid = safeStr(portalMeta.fileId || portalMeta.id || canonical.id);
  const storagePath = resolveCanonicalProductClientFileStoragePath(canonical);
  const meta = {
    ...portalMeta,
    id: fid || portalMeta.id,
    fileId: fid || portalMeta.fileId,
    projectId: safeStr(portalMeta.projectId || canonical.projectId) || undefined,
  };
  if (storagePath) {
    meta.storagePath = storagePath;
    meta.path = storagePath;
    if (isVersionedPmProjectFileStoragePath(storagePath)) {
      meta.storageSchemaVersion = Math.max(
        Number(meta.storageSchemaVersion || 0),
        Number(canonical.storageSchemaVersion || 0) || 2
      );
    }
  }
  const thumb = safeStr(canonical.thumbnailStoragePath || canonical.thumbnailPath);
  if (thumb) meta.thumbnailStoragePath = thumb;
  const url = safeStr(canonical.url);
  if (url) meta.url = url;
  if (Number(canonical.storageSchemaVersion || 0) >= 2) {
    meta.storageSchemaVersion = Number(canonical.storageSchemaVersion);
  }
  if (canonical.storagePublicationState) {
    meta.storagePublicationState = canonical.storagePublicationState;
  }
  return meta;
}
